import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { makeSocket } from "@/lib/socket";
import { toast } from "sonner";

const GameContext = createContext(null);

const POLL_MS = 2000;
const DROP_FEED_MAX = 40;

export function GameProvider({ children }) {
  const [content, setContent] = useState(null);
  const [snap, setSnap] = useState(null);
  const [feed, setFeed] = useState([]);
  const [chat, setChat] = useState([]);
  const [online, setOnline] = useState(1);
  const [worldNodes, setWorldNodes] = useState(null);
  const [mapOwnership, setMapOwnership] = useState({}); // region_id → owner
  const [socketReady, setSocketReady] = useState(false);
  const socketRef = useRef(null);
  const feedIdRef = useRef(0);
  const seenQuestEvents = useRef(new Set());

  const pushFeed = useCallback((items) => {
    if (!items?.length) return;
    setFeed((prev) => {
      const stamped = items.map((it) => ({ ...it, _id: ++feedIdRef.current, at: Date.now() }));
      const next = [...stamped, ...prev];
      return next.slice(0, DROP_FEED_MAX);
    });
    // Toast quest milestones (deduped by event key so re-polls don't re-fire).
    for (const ev of items) {
      if (ev.kind === "quest_step") {
        const key = `s:${ev.quest}:${ev.step_idx}`;
        if (!seenQuestEvents.current.has(key)) {
          seenQuestEvents.current.add(key);
          toast.success("Quest step complete!", { description: ev.step_id?.replace(/_/g, " ") });
        }
      } else if (ev.kind === "quest_complete") {
        const key = `c:${ev.quest}`;
        if (!seenQuestEvents.current.has(key)) {
          seenQuestEvents.current.add(key);
          toast.success("Quest completed!", { description: ev.quest.replace(/_/g, " ") });
        }
      }
    }
  }, []);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/state");
      setSnap(data);
      if (data.events?.length) pushFeed(data.events);
    } catch (e) {
      // Silent — likely auth expired
    }
  }, [pushFeed]);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/content");
      setContent(data);
    } catch (e) { /* noop */ }
  }, []);

  const fetchChat = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/chat/recent");
      setChat(data.messages || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    fetchContent();
    // Wait for the first authoritative state before opening the socket,
    // so the httpOnly access_token cookie is guaranteed to be in the browser jar
    // and the initial handshake succeeds on the first attempt.
    (async () => {
      await fetchState();
      await fetchChat();
      setSocketReady(true);
    })();
    const iv = setInterval(fetchState, POLL_MS);
    return () => clearInterval(iv);
  }, [fetchContent, fetchState, fetchChat]);

  useEffect(() => {
    if (!socketReady) return undefined;
    const s = makeSocket((msg) => {
      if (msg.kind === "chat") {
        setChat((prev) => [...prev.slice(-39), msg.message]);
      } else if (msg.kind === "presence" || msg.kind === "hello") {
        if (typeof msg.online === "number") setOnline(msg.online);
      } else if (msg.kind === "events") {
        pushFeed(msg.events);
      } else if (msg.kind === "world_state") {
        setWorldNodes(msg.nodes);
      } else if (msg.kind === "country_color") {
        // A country changed its map colour — repaint every region held by that iso.
        setMapOwnership((prev) => {
          const next = { ...prev };
          for (const rid of Object.keys(next)) {
            if (next[rid]?.holder_iso === msg.iso) {
              next[rid] = { ...next[rid], faction_color: msg.color };
            }
          }
          return next;
        });
      } else if (msg.kind === "map_ownership") {
        // Live push whenever any player claims/abandons a region.
        setMapOwnership((prev) => {
          const next = { ...prev };
          if (msg.owner) {
            next[msg.region_id] = {
              holder_iso:    msg.owner.holder_iso,
              holder_name:   msg.owner.holder_name || msg.owner.name,
              faction_color: msg.owner.faction_color,
              claimed_at:    msg.owner.claimed_at,
              claimed_via:   msg.owner.claimed_via || "claim",
            };
          } else {
            delete next[msg.region_id];
          }
          return next;
        });
      }
    });
    socketRef.current = s;
    return () => { s.close(); };
  }, [pushFeed, socketReady]);

  const startAction = useCallback(async (actionId) => {
    const { data } = await api.post("/api/game/action/start", { action_id: actionId });
    setSnap(data);
  }, []);
  const stopAction = useCallback(async () => {
    const { data } = await api.post("/api/game/action/stop");
    setSnap(data);
  }, []);
  const startCombat = useCallback(async (monsterId) => {
    const { data } = await api.post("/api/game/combat/start", { monster_id: monsterId });
    setSnap(data);
  }, []);
  const bankDeposit = useCallback(async (item, qty) => {
    const { data } = await api.post("/api/game/bank/deposit", { item, qty });
    setSnap(data);
  }, []);
  const bankWithdraw = useCallback(async (item, qty) => {
    const { data } = await api.post("/api/game/bank/withdraw", { item, qty });
    setSnap(data);
  }, []);
  const bankDepositAll = useCallback(async () => {
    const { data } = await api.post("/api/game/bank/deposit-all");
    setSnap(data);
  }, []);
  const equip = useCallback(async (item, slot) => {
    const { data } = await api.post("/api/game/equip", { item, slot });
    setSnap(data);
  }, []);
  const unequip = useCallback(async (slot = "weapon") => {
    const { data } = await api.post("/api/game/unequip", { slot });
    setSnap(data);
  }, []);
  const setCombatStyle = useCallback(async (style) => {
    const { data } = await api.post("/api/game/combat/style", { style });
    setSnap(data);
  }, []);
  const setActiveSpell = useCallback(async (spell_id) => {
    const { data } = await api.post("/api/game/combat/spell", { spell_id });
    setSnap(data);
  }, []);
  const newSlayerTask = useCallback(async () => {
    const { data } = await api.post("/api/game/slayer/new-task");
    setSnap((prev) => (prev ? { ...prev, slayer_task: data.task } : prev));
  }, []);
  const buildRoom = useCallback(async (room_type) => {
    const { data } = await api.post("/api/game/house/build", { room_type });
    setSnap(data);
  }, []);
  const changeTheme = useCallback(async (theme_id) => {
    const { data } = await api.post("/api/game/house/theme", { theme_id });
    setSnap(data);
  }, []);
  // Grand Exchange
  const gePlaceOrder = useCallback(async (order) => {
    const { data } = await api.post("/api/game/ge/order", order);
    setSnap(data.state);
    return data.order;
  }, []);
  const geCancel = useCallback(async (order_id) => {
    const { data } = await api.post("/api/game/ge/cancel", { order_id });
    setSnap(data.state);
    return data.order;
  }, []);
  const geClaim = useCallback(async () => {
    const { data } = await api.post("/api/game/ge/claim");
    setSnap(data.state);
    return data;
  }, []);
  const sendChat = useCallback(async (text) => {
    await api.post("/api/game/social/chat/send", { text, channel: "world" });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/state");
      setSnap(data);
      if (data.events?.length) pushFeed(data.events);
    } catch (e) { /* silent */ }
  }, [pushFeed]);

  const value = useMemo(
    () => ({
      content, snap, feed, chat, online, worldNodes, mapOwnership, setMapOwnership,
      fetchState, refresh, startAction, stopAction, startCombat,
      bankDeposit, bankWithdraw, bankDepositAll, equip, unequip, sendChat,
      setCombatStyle, setActiveSpell, newSlayerTask,
      buildRoom, changeTheme,
      gePlaceOrder, geCancel, geClaim,
    }),
    [content, snap, feed, chat, online, worldNodes, mapOwnership,
      fetchState, refresh, startAction, stopAction, startCombat,
      bankDeposit, bankWithdraw, bankDepositAll, equip, unequip, sendChat,
      setCombatStyle, setActiveSpell, newSlayerTask,
      buildRoom, changeTheme,
      gePlaceOrder, geCancel, geClaim],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside GameProvider");
  return ctx;
}
